import {
  ActiveIndicator,
  Badge,
  cx,
  DesktopSidebar,
  DialogTrigger,
  Drawer,
  HopexEmblem,
  HopexLockup,
  IconButton,
  navItemClass,
  SidebarSectionLabel,
  ThemeToggle,
  TopBar,
} from '@hopex/ui';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronDown, LogOut, Menu, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import type { AdminMe } from '../api.ts';
import { useAdminSession } from '../session.tsx';
import {
  ADMIN_NAV_COMMS,
  ADMIN_NAV_CRYPTO,
  ADMIN_NAV_FUTURE,
  ADMIN_NAV_LIVE,
  ADMIN_NAV_MONEY,
  ADMIN_NAV_REPORTS,
  ADMIN_NAV_SYSTEM,
  ADMIN_NAV_TRADING,
  isFutureSection,
  type AdminNavItem,
} from './nav.ts';
import { AdminSearch } from './Search.tsx';

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

function NavLink({
  item,
  pathname,
  onNavigate,
}: {
  item: AdminNavItem;
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  const active = isActive(pathname, item.to);
  const Icon = item.icon;
  // Built sections are live; the not-yet-built ones (future group) are quieter and say «به‌زودی».
  const later = isFutureSection(item.to);
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cx(
        navItemClass(active),
        later ? 'h-8 text-[12.5px]' : 'h-10 text-[13.5px]',
        later && !active && 'text-fg-subtle',
      )}
      data-nav-phase={item.phase}
      {...(onNavigate ? { onClick: onNavigate } : {})}
    >
      <ActiveIndicator active={active} />
      <Icon
        aria-hidden
        className={cx(later ? 'size-4' : 'size-[18px]', 'shrink-0', active && 'text-accent-text')}
        strokeWidth={1.9}
      />
      <span className="truncate">{item.label}</span>
      {later && (
        <span className="ms-auto rounded-xs border border-line px-1 text-[10px] font-medium text-fg-subtle">
          به‌زودی
        </span>
      )}
    </Link>
  );
}

/**
 * Live operations first; the later-phase sections (routes still exist) sit inside ONE collapsed
 * «ماژول‌های آینده» disclosure — expanded automatically only when such a page is open.
 */
function AdminNavLinks({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) {
  const inFuture = ADMIN_NAV_FUTURE.some((g) => g.items.some((i) => isActive(pathname, i.to)));
  const [open, setOpen] = useState(inFuture);
  const expanded = open || inFuture;
  return (
    <>
      <SidebarSectionLabel>عملیات</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-live>
        {ADMIN_NAV_LIVE.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      <SidebarSectionLabel>مالی</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-money>
        {ADMIN_NAV_MONEY.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      <SidebarSectionLabel>رمزارز</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-crypto>
        {ADMIN_NAV_CRYPTO.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      <SidebarSectionLabel>بازار و تبدیل</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-trading>
        {ADMIN_NAV_TRADING.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      <SidebarSectionLabel>ارتباطات و پشتیبانی</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-comms>
        {ADMIN_NAV_COMMS.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      <SidebarSectionLabel>گزارش و رشد</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-reports>
        {ADMIN_NAV_REPORTS.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      <SidebarSectionLabel>سیستم</SidebarSectionLabel>
      <div className="flex flex-col gap-0.5" data-nav-system>
        {ADMIN_NAV_SYSTEM.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
      </div>
      {ADMIN_NAV_FUTURE.length > 0 && (
        <div className="mt-4 border-t border-line pt-2">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="hx-future-modules"
            onClick={() => setOpen((o) => !o)}
            className="flex h-9 w-full cursor-pointer items-center justify-between rounded-md px-3 text-[12px] font-semibold text-fg-subtle outline-hidden hover:bg-surface-2 hover:text-fg-muted focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
            data-nav-future-toggle
          >
            ماژول‌های آینده
            <ChevronDown
              aria-hidden
              className={cx('size-4 transition-transform', expanded && 'rotate-180')}
            />
          </button>
          {expanded && (
            <div id="hx-future-modules" data-nav-future>
              {ADMIN_NAV_FUTURE.map((group) => (
                <div key={group.label}>
                  <SidebarSectionLabel>{group.label}</SidebarSectionLabel>
                  <div className="flex flex-col gap-0.5">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        item={item}
                        pathname={pathname}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
export function AdminShell({ me }: { me: AdminMe }) {
  const { signOut } = useAdminSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex min-h-dvh bg-canvas text-fg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-[70] focus:rounded-md focus:bg-surface-2 focus:px-4 focus:py-2"
      >
        پرش به محتوا
      </a>
      <DesktopSidebar
        label="ناوبری مدیریت"
        header={
          <Link
            to="/"
            aria-label="اتاق کنترل — داشبورد"
            className="flex items-center gap-3 text-fg"
          >
            <HopexLockup size={22} decorative />
            <span className="rounded-xs border border-line px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              ADMIN
            </span>
          </Link>
        }
      >
        <AdminNavLinks pathname={pathname} />
        <div className="h-6" />
      </DesktopSidebar>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar>
          {/* Below 1024px the sidebar is hidden; the same navigation opens in a start-side drawer. */}
          <div className="lg:hidden">
            <DialogTrigger isOpen={menuOpen} onOpenChange={setMenuOpen}>
              <IconButton label="منوی مدیریت" variant="ghost">
                <Menu aria-hidden className="size-5" />
              </IconButton>
              <Drawer title="ناوبری مدیریت" side="start">
                <nav aria-label="ناوبری مدیریت" className="-mx-3">
                  <AdminNavLinks pathname={pathname} onNavigate={() => setMenuOpen(false)} />
                </nav>
              </Drawer>
            </DialogTrigger>
          </div>
          <span className="text-fg lg:hidden">
            <HopexEmblem size={26} decorative />
          </span>
          <Badge tone="accent">محیط: توسعه‌ی محلی</Badge>
          <div className="mx-4 flex min-w-0 flex-1 justify-center">
            <AdminSearch />
          </div>
          <div className="ms-auto flex items-center gap-3">
            <span
              className="hidden items-center gap-1.5 text-xs text-fg-muted md:inline-flex"
              data-admin-chip
            >
              <ShieldCheck aria-hidden className="size-4" />
              {me.displayName}
              <bdi dir="ltr" className="font-mono text-fg-subtle">
                ({me.username})
              </bdi>
            </span>
            <ThemeToggle compact />
            <IconButton label="خروج از اتاق کنترل" variant="ghost" onPress={() => void signOut()}>
              <LogOut aria-hidden className="size-[18px]" />
            </IconButton>
          </div>
        </TopBar>
        <main id="main" className="flex-1 py-6 lg:py-8">
          <div key={pathname} className="hx-page-enter">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
